import React, { useState, useEffect } from 'react';
import { Layout, Divider, Row, Col, message, notification, Grid } from 'antd';
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import {UndoRounded, StarRounded, WarningRounded, AppsRounded} from "@mui/icons-material";
import {useDispatch, useSelector} from "react-redux";
import PageHeader from "../../layouts/PageHeader";
import BuilderTable from "./components/BuilderTable";
import BuilderToolbar from "./components/BuilderToolbar";
import PortfolioDetails from "./components/PortfolioDetails";
import {iconMapping} from "../../configs/AppsConfig";
import { ensureUniqueItems, handleLockAll, handleUnlockAll, handleScaleDown, handleScaleUp,
         handleProRataFill, handleEqualFill, reformatWeightMultiple } from "./utils";
import {createNewPortfolio, updatePortfolioPositions} from "../../api/portfolioApi";
import {fetchBlendedTimeseries} from "../../api/timeseriesApi";
import {addBlendedTimeseries, clearBlendedTimeseries} from "../../store/slices/timeseries.slice";
import TimeseriesBlender from "../TimeseriesBlender/TimeseriesBlender";
import {saveBuilderState} from "../../store/slices/builder.slice";
import {v4 as uuidv4} from 'uuid';
import {useTickerData} from "../../hooks/useTickerData";
import _ from 'lodash';
import BuilderResult     from "./components/BuilderResult";
import { resetPortfolioBuilderContext } from "../../store/slices/builder.slice";
import Placeholder from "../../components/Placeholder";
import {DoneRounded} from "@mui/icons-material";
const { useBreakpoint } = Grid;
const formatItem = (id, name, type, wgt, logo, isLocked, flagForRemoval) => {
    return {
        id, name, type, wgt, isLocked, logo,
        _locked: isLocked,
        _flagForRemoval: flagForRemoval
    }
}

const NULL_SYMBOLS = [
    'custodial_cash'
]

const calculateTotalWeight = (items) => {
    return items.reduce((acc, item) => acc + parseFloat(item.wgt || 0), 0);
};



const computeWeightedValue = (tickerData, portfolioItems, path, prec=2) => {
    const weightedTotal = [...portfolioItems].filter(x=>!NULL_SYMBOLS.includes(x.id)).reduce((total, item) => {
        const ticker = item.id;
        const weight = item.wgt;
        const nestedValue = _.get(tickerData[ticker], path);
        return total + (nestedValue * weight);
    }, 0);
    return weightedTotal?.toFixed(prec) ?? 0;

};

/**
 *
 * @param callbackOnFinish {function} - Callback function to override the default save behaviour of portfolios. The
 *          ...callback should accept the portfolio items as an argument.
 * @param initialItemList {Array} - An array of portfolio items to initialize the portfolio builder with.
 * @returns {JSX.Element}
 * @constructor
 */
const PortfolioBuilder = ({ callbackOnFinish=null, initialItemList, isChild=false}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();
    const [api, contextHolder] = notification.useNotification();
    const [showAlert, setShowAlert] = useState(false);

    const portfolioBuilder = useSelector(state=>state.builder.portfolioBuilder);
    const [portfolioItems, setPortfolioItems] = useState(initialItemList || portfolioBuilder.items);
    const [portfolioTitle, setPortfolioTitle] = useState(portfolioBuilder?.title ?? "");
    const [currentView, setCurrentView] = useState('main');
    const [showStats, setShowStats] = useState(true);
    const [stateId, setStateId] = useState(portfolioBuilder?.stateId || uuidv4());
    const [showResult, setShowResult] = useState(false);
    const [portfolioStats, setPortfolioStats] = useState({
        threeMonthVol: 0,
        threeMonthBattingAvg: 0,
        oneYearCorrSpy: 0,
        oneYearCorrEfa: 0,
        oneYearBetaSpy: 0
    })
    const isNew = portfolioBuilder.id === null;
    const symbols = portfolioItems.map(item => item.id);
    const fields = ['stats.windows.3_mo.hv', 'stats.windows.3_mo.batting_average', 'stats.benchmarks.spy.corr_1_yr', 'stats.benchmarks.efa.corr_1_yr', 'stats.benchmarks.spy.beta_1_yr'];
    const tickerData = useTickerData(symbols, fields, 'stats');

    const onSuccess =()=>{
        notification.success({
          message: `${portfolioTitle} has been saved!`,
          description: 'You can view it here',
          icon: (
            <DoneRounded
              style={{
                color: '#108ee9',
              }}
            />
          ),
          onClick: ()=>{
              navigate('/portfolios')
          }
        });
    }

    const handleCompute = () => {
        setPortfolioStats({
            threeMonthVol: computeWeightedValue(tickerData, portfolioItems, 'stats.windows.3_mo.hv'),
            threeMonthBattingAvg: computeWeightedValue(tickerData, portfolioItems, 'stats.windows.3_mo.batting_average') ,
            oneYearCorrSpy: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.spy.corr_1_yr'),
            oneYearCorrEfa: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.efa.corr_1_yr'),
            oneYearBetaSpy: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.spy.beta_1_yr'),
            totalWeight: calculateTotalWeight(portfolioItems)
        });
    };

    useEffect(() => {
        if (tickerData && Object.keys(tickerData).length > 0 || portfolioItems && Object.keys(portfolioItems).length > 0) {
            handleCompute();
        }
    }, [JSON.stringify(tickerData), JSON.stringify(portfolioItems)]);

    useEffect(() => {
        if (portfolioItems.length > 0 || portfolioTitle) {
            dispatch(saveBuilderState({
                builderId: stateId,
                builderState: {
                    id: portfolioBuilder.id,
                    items: portfolioItems,
                    title: portfolioTitle,
                    type: portfolioBuilder.type,
                    stateId: stateId
                }
            }));
        }
    }, [portfolioItems, portfolioTitle, dispatch, stateId]);


    const handlePortfolioSelections = (portfolios) => {
        const newItems = portfolios.map((x) => formatItem(x.id, x.name, x.type, 0, '', false, false));
        const uniqueItems = ensureUniqueItems(newItems, portfolioItems);
        setPortfolioItems((prevItems) => [...prevItems, ...uniqueItems]);
    };

    const handleTickerSelections = (items) => {
        const newItems = items.map((x) => formatItem(x.symbol, x.name, x.type, 0, x?.logo, false, false));
        const uniqueItems = ensureUniqueItems(newItems, portfolioItems);
        setPortfolioItems((prevItems) => [...prevItems, ...uniqueItems])
    };


    const handleFetchBlendedTimeseries = () => {
        // Create a mapping string from the portfolio items
        const mapping = portfolioItems
            .filter(item => item.wgt > 0 && item.id!=='custodial_cash')  // Ensure only items with weights are included
            .map(item => `${item.id}:${item.wgt}`)
            .join(',');
        if (mapping) {
            dispatch(fetchBlendedTimeseries({ mapping }))
                .unwrap()
                .then(response => {
                    dispatch(addBlendedTimeseries({
                        name: `Blended Series - ${new Date().toLocaleTimeString()}`,
                        data: response.series,  // Assume this is the correct structure
                        mapping
                    }));
                    message.success('Blended timeseries fetched successfully');
                })
                .catch(error => {
                    message.error('Failed to fetch blended timeseries');
                });
        } else {
            message.error('No valid items with weights to fetch timeseries');
        }
    };

    const handleTitleChange = (title) => {
        setPortfolioTitle(title);
    };

    const handleSave = ({saveAsNew=true}) => {
        let type = portfolioBuilder?.type || 'm';
        if (type?.length > 1){
            type = type.slice(0, 1).toString();
        }

        if (isNew || saveAsNew===true) {
            dispatch(createNewPortfolio({
                title: portfolioTitle,
                type: type,
                itemArray: reformatWeightMultiple(portfolioItems)
            })).then((resp)=>{
                onSuccess()
            })
            message.info(`${portfolioTitle} is being created... You will be notified when it is ready`);
        }
        else {
            dispatch(updatePortfolioPositions({
                portfolioId: portfolioBuilder.id,
                itemArray: reformatWeightMultiple(portfolioItems)
            })).then((resp)=>{
                onSuccess()
            })
        }
    };

    const handleResetPortfolioBuilderContext = ()=>{
        dispatch(resetPortfolioBuilderContext(portfolioBuilder.type))
        setPortfolioTitle('');
        setPortfolioItems([])
    }

    const PortfolioStatusMessage = () => (
        <Alert
            className={'mb-2'}
            icon={isNew ? <StarRounded /> : <WarningRounded />}
            color={isNew?'info': 'error'}
            variant={isNew?'text':'outlined'}
            label={isNew ? "New Portfolio" : "Existing Portfolio"}
            onClose={()=>setShowAlert(false)}
        >
            {isNew ? "This is a new portfolio. Click 'Create Portfolio' to save it." : "This is an existing portfolio. Click 'Update Existing' to save changes."}
        </Alert>
    );
    let callback = callbackOnFinish ? ()=>{callbackOnFinish(reformatWeightMultiple(portfolioItems))} : handleSave;



    return (
        <>
            {!isChild && (
                <PageHeader
                        title={'Portfolio Builder'}
                        logoUrl={iconMapping.portfolioBuilder}
                        actions={[
                            <Button startIcon={<AppsRounded/>} variant={'text'} onClick={()=>navigate('/builders/landing')}>All Drafts</Button>,
                            <Button startIcon={<UndoRounded/>} color={'error'} variant={'text'} onClick={()=>handleResetPortfolioBuilderContext()}>
                                Reset
                            </Button>,
                        ]}
                    />
            )}
            <Layout
                style={{
                    height: !isChild ? '100vh' : 'auto',
                    overflow: 'scroll',
                }}
            >
                {currentView==='main'&&(
                    <>
                        <Layout.Content style={{padding: '2.5%'}} className={'w-full'}>
                            {showAlert && (
                                <PortfolioStatusMessage/>
                            )}
                            <PortfolioDetails
                                title={portfolioTitle}
                                onTitleChange={handleTitleChange}
                                onSave={callback}
                                isNew={isNew}
                                portfolioStats={portfolioStats}
                                showStats={showStats}
                                setShowStats={setShowStats}
                                direction={'row'}
                            />
                            <Row gutter={32}>
                                <Col flex={'1'}>
                                    <div style={{minHeight: '60vh', padding: '20px 0'}}>
                                        <BuilderToolbar
                                            portfolioType={portfolioBuilder.type}
                                            onEqualWeight={() => {
                                                handleEqualFill(portfolioItems, setPortfolioItems)
                                            }}
                                            onLockAll={() => {
                                                handleLockAll(portfolioItems, setPortfolioItems)
                                            }}
                                            onUnlockAll={() => {
                                                handleUnlockAll(portfolioItems, setPortfolioItems)
                                            }}
                                            onProRataFill={() => {
                                                handleProRataFill(portfolioItems, setPortfolioItems)
                                            }}
                                            onScaleUp={(scaleValue) => handleScaleUp(portfolioItems, setPortfolioItems, scaleValue)}
                                            onScaleDown={(scaleValue) => handleScaleDown(portfolioItems, setPortfolioItems, scaleValue)}
                                            onTickerSearch={handleTickerSelections}
                                            onPortfolioSelection={handlePortfolioSelections}
                                            onFetchBlendedTimeseries={() => {
                                                handleFetchBlendedTimeseries();
                                                setCurrentView('blender')
                                            }}
                                        />
                                        <BuilderTable items={portfolioItems} onItemsUpdate={setPortfolioItems}
                                                      itemStats={tickerData}/>
                                        <Divider/>
                                        {portfolioItems.length === 1 && (<Placeholder text={'No Holdings Added yet'}/>)}
                                    </div>
                                </Col>
                            </Row>

                            <Divider/>

                        </Layout.Content>
                    </>
                )}
                {currentView === 'blender' && (
                    <TimeseriesBlender/>

                )}
            </Layout>
        </>
    );
};

export default PortfolioBuilder;
